import React from "react";
import { Box, Typography } from "@mui/material";

import CultureHubRow from "./CultureHubRow";
import visuallyHiddenSx from "../../../utils/a11y/visuallyHiddenSx";

const SECTION_LABELS = {
  Assessment: "Pareceres",
  Addendum: "Adendos",
  Episode: "Episódios",
  Chapter: "Capítulos",
  OtherType: "Outros materiais",
};

// the leading checkbox column has no visible heading, so AT gets one
const SelectionHeaderCell = () => (
  <Box role="columnheader">
    <Box component="span" sx={visuallyHiddenSx}>
      Selecionar
    </Box>
  </Box>
);

const GRID_COLUMNS = "48px 176px minmax(220px, 1fr) 116px 146px 142px 90px";

const ASSESSMENT_GRID_COLUMNS = "48px 1fr 90px";

const ADDENDUM_GRID_COLUMNS = "48px minmax(260px, 1fr) 260px 220px 90px";

// sum of the grid columns plus the row's horizontal padding; below this the group scrolls horizontally
export const TABLE_MIN_WIDTHS = {
  Assessment: 360,
  Addendum: 902,
  Episode: 962,
  Chapter: 962,
  OtherType: 962,
};

const CultureHubTable = ({
  section,
  groupLabel,
  selectedIds,
  onToggleAttachment,
  onDeleteAssessment,
}) => {
  const isAssessment = section.kind === "Assessment";
  const isAddendum = section.kind === "Addendum";
  const isEpisode = section.kind === "Episode";
  const isChapter = section.kind === "Chapter";
  const isOtherType = section.kind === "OtherType";

  const tableProps = {
    role: "table",
    "aria-label": [groupLabel, SECTION_LABELS[section.kind]]
      .filter(Boolean)
      .join(": "),
    "aria-rowcount": section.attachments.length + 1,
  };

  /**
   * PARECERES
   */
  if (isAssessment) {
    return (
      <Box {...tableProps}>
        <Box
          role="row"
          sx={{
            minHeight: "38px",
            px: 1.5,
            display: "grid",
            gridTemplateColumns: ASSESSMENT_GRID_COLUMNS,
            alignItems: "center",
            bgcolor: "#E4F6FD",
            borderTop: "1px solid #DDDDDD",
            borderBottom: "1px solid #DDDDDD",
          }}
        >
          <SelectionHeaderCell />

          <Typography role="columnheader" sx={headerStyle}>
            Pareceres
          </Typography>

          <Typography
            role="columnheader"
            sx={{
              ...headerStyle,
              textAlign: "center",
            }}
          >
            Opções
          </Typography>
        </Box>

        {section.attachments.map((attachment) => (
          <CultureHubRow
            key={attachment.id}
            kind={section.kind}
            attachment={attachment}
            selected={selectedIds.includes(attachment.id)}
            onToggle={() => onToggleAttachment(attachment.id)}
            onDeleteAssessment={onDeleteAssessment}
          />
        ))}
      </Box>
    );
  }

  /**
   * ADENDOS
   */
  if (isAddendum) {
    return (
      <Box {...tableProps}>
        <Box
          role="row"
          sx={{
            minHeight: "36px",
            px: 1.5,
            display: "grid",
            gridTemplateColumns: ADDENDUM_GRID_COLUMNS,
            alignItems: "center",
            bgcolor: "#F7F7F7",
            borderTop: "1px solid #DDDDDD",
            borderBottom: "1px solid #DDDDDD",
          }}
        >
          <SelectionHeaderCell />

          <Typography role="columnheader" sx={headerStyle}>
            Arquivo
          </Typography>

          <Typography
            role="columnheader"
            sx={{
              ...headerStyle,
              textAlign: "center",
            }}
          >
            Relação
          </Typography>

          <Typography
            role="columnheader"
            sx={{
              ...headerStyle,
              textAlign: "center",
            }}
          >
            Liberado em
          </Typography>

          <Typography
            role="columnheader"
            sx={{
              ...headerStyle,
              textAlign: "center",
            }}
          >
            Opções
          </Typography>
        </Box>

        {section.attachments.map((attachment) => (
          <CultureHubRow
            key={attachment.id}
            kind={section.kind}
            attachment={attachment}
            selected={selectedIds.includes(attachment.id)}
            onToggle={() => onToggleAttachment(attachment.id)}
          />
        ))}
      </Box>
    );
  }

  /**
   * CAPÍTULOS / EPISÓDIOS / OUTROS MATERIAIS
   */
  return (
    <Box {...tableProps}>
      <Box
        role="row"
        sx={{
          minHeight: "36px",
          px: 1.5,
          display: "grid",
          gridTemplateColumns: GRID_COLUMNS,
          alignItems: "center",
          bgcolor: "#F7F7F7",
          borderTop: "1px solid #DDDDDD",
          borderBottom: "1px solid #DDDDDD",
        }}
      >
        <SelectionHeaderCell />

        <Typography
          role="columnheader"
          sx={{
            ...headerStyle,
            textAlign: "center",
          }}
        >
          {isEpisode
            ? "Número"
            : isChapter
              ? "Capítulo"
              : isOtherType
                ? "Tipo"
                : ""}
        </Typography>

        <Typography role="columnheader" sx={headerStyle}>
          Arquivo
        </Typography>

        <Typography role="columnheader" sx={headerStyle}>
          Versão
        </Typography>

        <Typography role="columnheader" sx={headerStyle}>
          Liberado em
        </Typography>

        <Typography role="columnheader" sx={headerStyle}>
          Planejado para
        </Typography>

        <Typography
          role="columnheader"
          sx={{
            ...headerStyle,
            textAlign: "center",
          }}
        >
          Opções
        </Typography>
      </Box>

      {section.attachments.map((attachment) => (
        <CultureHubRow
          key={attachment.id}
          kind={section.kind}
          attachment={attachment}
          selected={selectedIds.includes(attachment.id)}
          onToggle={() => onToggleAttachment(attachment.id)}
        />
      ))}
    </Box>
  );
};

const headerStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#929292",
};

export default CultureHubTable;
